import { useEffect, useState } from "react";
import api from "../lib/api";
import { fmt, fmtDate, ESTADO_COLOR, ESTADO_LABEL } from "../lib/utils";
import { StatCard, Card, CardHeader, CardTitle, CardContent, Badge, Spinner, Empty } from "../components/ui";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Wrench, CheckCircle2, FileText, DollarSign, Clock, AlertTriangle } from "lucide-react";

const ESTADO_CHART_COLOR = {
  PENDIENTE:        "#94a3b8",
  EN_PROCESO:       "hsl(var(--primary))",
  CONTROL_CALIDAD:  "hsl(var(--warning))",
  LISTO:            "hsl(var(--success))",
  ENTREGADO:        "#6ee7b7",
};

export default function Dashboard() {
  const [kpis,    setKpis]    = useState(null);
  const [barra,   setBarra]   = useState([]);
  const [ingresos,setIngresos] = useState([]);
  const [urgentes,setUrgentes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/kpis"),
      api.get("/dashboard/trabajos-por-estado"),
      api.get("/dashboard/ingresos-mensuales"),
      api.get("/dashboard/urgentes"),
    ]).then(([k, b, i, u]) => {
      setKpis(k.data);
      setBarra(b.data);
      setIngresos(i.data);
      setUrgentes(u.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner className="w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-screen-xl">
      <div>
        <h1 className="font-display font-bold text-xl">Dashboard</h1>
        <p className="text-sm text-muted-foreground">Resumen operativo del taller</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard
          title="Trabajos activos"
          value={kpis?.activas ?? 0}
          icon={Wrench}
        />
        <StatCard
          title="Entregados hoy"
          value={kpis?.entregadas_hoy ?? 0}
          icon={CheckCircle2}
        />
        <StatCard
          title="Presupuestos pend."
          value={kpis?.presupuestos_pendientes ?? 0}
          icon={FileText}
        />
        <StatCard
          title="Facturado este mes"
          value={fmt(kpis?.facturacion_mes ?? 0)}
          icon={DollarSign}
          className="lg:col-span-1"
        />
        <StatCard
          title="Pendiente cobro"
          value={fmt(kpis?.pendientes_cobro ?? 0)}
          icon={DollarSign}
          accent
        />
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Trabajos por estado */}
        <Card>
          <CardHeader>
            <CardTitle>Trabajos por estado</CardTitle>
          </CardHeader>
          <CardContent>
            {barra.length === 0 ? (
              <Empty message="Sin datos" />
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={barra} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                  <XAxis dataKey="estado" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 6 }}
                    formatter={(v) => [v, "Cantidad"]}
                  />
                  <Bar dataKey="cantidad" radius={[4, 4, 0, 0]}>
                    {barra.map((entry, i) => (
                      <Cell key={i} fill={ESTADO_CHART_COLOR[entry.estado] ?? "#94a3b8"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Ingresos mensuales */}
        <Card>
          <CardHeader>
            <CardTitle>Ingresos mensuales</CardTitle>
          </CardHeader>
          <CardContent>
            {ingresos.length === 0 ? (
              <Empty message="Sin datos de facturación" />
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={ingresos} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                  <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => `${(v / 1_000_000).toFixed(1)}M`}
                  />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 6 }}
                    formatter={(v) => [fmt(v), "Facturado"]}
                  />
                  <Bar dataKey="total" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Urgentes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-accent" />
            Trabajos urgentes / vencidos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {urgentes.length === 0 ? (
            <Empty message="No hay trabajos vencidos" icon={CheckCircle2} />
          ) : (
            <div className="space-y-2">
              {urgentes.map((ot) => (
                <div
                  key={ot.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-md border border-border p-3"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{ot.vehiculo_desc}</p>
                    <p className="text-xs text-muted-foreground">{ot.cliente_nombre}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <div>
                      <p className="text-xs text-muted-foreground">Técnico</p>
                      <p className="text-xs font-medium">{ot.tecnico_nombre ?? "—"}</p>
                    </div>
                    <div className="flex items-center gap-1 text-accent text-xs font-medium">
                      <Clock className="w-3.5 h-3.5" />
                      {fmtDate(ot.fecha_estimada)}
                    </div>
                    <Badge className={ESTADO_COLOR[ot.estado]}>
                      {ESTADO_LABEL[ot.estado] ?? ot.estado}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
